import { GearItem, Rarity } from "@highmarch/domain";

/**
 * Hold-to-confirm (the Armoury's salvage buttons): the action runs only after the finger stays down for `ms`, while a
 * bar fills across the button, so a stray tap on a list can never salvage anything. A quick tap nudges the button
 * (the label says "Hold"), lifting, sliding off, a scroll taking over (pointercancel) or losing focus all let go.
 * Keyboard: holding Enter or Space works the same way. DOM only; the fill is a transform, so it composites.
 */

/** Hold length per rarity (Common … Mythic): the rarer the item, the longer the deliberate press (concept §3). */
export const HOLD_MS_BY_RARITY: readonly number[] = [550, 650, 750, 1100, 1300, 1500];
/** Any upgraded item holds at least this long: its upgrades were paid for. */
export const HOLD_MS_UPGRADED = 1100;
/** A finger that drifts further than this (px) is scrolling, not holding. */
export const HOLD_SLOP_PX = 14;
/** How fast the bar drains after an early release. */
export const HOLD_RELEASE_MS = 180;
/** How long the "hold, don't tap" nudge class stays on. */
const NUDGE_MS = 420;

/** How long salvaging this item asks the player to hold. */
export function holdMsFor(item: Pick<GearItem, "rarity" | "raised">): number {
  const base = HOLD_MS_BY_RARITY[item.rarity] ?? HOLD_MS_BY_RARITY[Rarity.Mythic];
  return item.raised > 0 ? Math.max(base, HOLD_MS_UPGRADED) : base;
}

export interface HoldOptions {
  /** How long the press must last. */
  readonly ms: number;
  /** Runs once when the hold completes. */
  readonly onDone: () => void;
  readonly className?: string;
  /** Screen-reader label (the visible label is the content). */
  readonly label?: string;
  /** Set when the action is not allowed (a locked item): the button stays visible, reads as off and never fires. */
  readonly disabled?: boolean;
  /** A quick tap instead of a hold (for a hint such as a toast). */
  readonly onTap?: () => void;
}

export interface HoldButton {
  readonly el: HTMLButtonElement;
  /** Lets go of a running hold (e.g. the panel closes). */
  cancel(): void;
  /** True while a press is filling the bar. */
  holding(): boolean;
}

interface PointerLike { readonly pointerType?: string; readonly button?: number; readonly clientX?: number; readonly clientY?: number; preventDefault?(): void }

/** `content` goes inside the button; the fill bar is added after it. */
export function holdButton(content: readonly (Node | string)[], opts: HoldOptions): HoldButton {
  const el = document.createElement("button");
  el.type = "button";
  el.className = `ahold${opts.className ? ` ${opts.className}` : ""}${opts.disabled ? " off" : ""}`;
  if (opts.label) el.setAttribute("aria-label", opts.label);
  if (opts.disabled) el.setAttribute("aria-disabled", "true");
  el.append(...content);
  const fill = document.createElement("span");
  fill.className = "fill";
  el.append(fill);

  let timer: number | null = null;
  let done = false;
  let x0 = 0, y0 = 0;
  let pressedAt = 0;

  const setFill = (on: boolean, ms: number) => {
    fill.style.transitionDuration = `${ms}ms`;
    fill.style.transform = on ? "scaleX(1)" : "scaleX(0)";
  };
  const start = (x: number, y: number) => {
    if (opts.disabled || done || timer !== null) return;
    x0 = x; y0 = y;
    pressedAt = Date.now();
    el.classList.add("holding");
    setFill(true, opts.ms);
    timer = window.setTimeout(finish, opts.ms);
  };
  const stop = () => {
    if (timer === null) return;
    window.clearTimeout(timer);
    timer = null;
    el.classList.remove("holding");
    setFill(false, HOLD_RELEASE_MS);
  };
  function finish(): void {
    timer = null;
    done = true;
    el.classList.remove("holding");
    el.classList.add("done");
    opts.onDone();
  }
  const nudge = () => {
    el.classList.add("nudge");
    window.setTimeout(() => el.classList.remove("nudge"), NUDGE_MS);
    opts.onTap?.();
  };

  el.addEventListener("pointerdown", (e: Event) => {
    const p = e as unknown as PointerLike;
    if (p.pointerType === "mouse" && (p.button ?? 0) !== 0) return;
    start(p.clientX ?? 0, p.clientY ?? 0);
  });
  el.addEventListener("pointermove", (e: Event) => {
    const p = e as unknown as PointerLike;
    if (timer !== null && Math.hypot((p.clientX ?? x0) - x0, (p.clientY ?? y0) - y0) > HOLD_SLOP_PX) stop();
  });
  const release = () => {
    // Let go before the end: a tap (well short of the hold) gets the hint; a long-but-short press just drains.
    const wasHolding = timer !== null;
    const short = wasHolding && Date.now() - pressedAt < opts.ms / 2;
    stop();
    if (short && !opts.disabled) nudge();
  };
  el.addEventListener("pointerup", release);
  el.addEventListener("pointercancel", stop);
  el.addEventListener("pointerleave", stop);
  el.addEventListener("blur", stop);
  // iOS long-press would otherwise open the callout / text selection over the button.
  el.addEventListener("contextmenu", (e: Event) => e.preventDefault());
  el.addEventListener("keydown", (e: Event) => {
    const k = e as KeyboardEvent;
    if ((k.key === "Enter" || k.key === " ") && !k.repeat) { k.preventDefault(); start(0, 0); }
  });
  el.addEventListener("keyup", (e: Event) => {
    const k = e as KeyboardEvent;
    if (k.key === "Enter" || k.key === " ") stop();
  });
  if (opts.disabled) el.addEventListener("click", () => opts.onTap?.());

  return { el, cancel: stop, holding: () => timer !== null };
}
